import { Box, DialogContent, Modal } from "@mui/material"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { usePageviewTracking } from "src/screens/Detail/use-pageview-tracking"
import { SettingsFocusKeys } from "src/screens/Settings/settings-focus-keys"

import { DGText } from "../../components/common/DGText"
import { Footer } from "../../components/common/Footer"
import { useModalDismiss } from "../../hooks/use-modal-dismiss"
import useSdk, { ErrorLevel } from "../../hooks/use-sdk"
import { useTransitions } from "../../hooks/use-transitions"
import useTranslate from "../../hooks/use-translate"
import { appStore, SetTrackingProps } from "../../store/AppStore"
import { comb, hwScroll, s } from "../../utilities/common"
import { theme, TI } from "../../utilities/theme"

import { PText, PTitle } from "./components/common"
import { ConsentButtons } from "./components/ConsentButtons"
import { FocusableParagraph } from "./components/FocusableParagraph"
import { SwitchBar } from "./components/SwitchBar"

export const PrivacySettingsModal = ({
  open,
  ...rest
}: {
  open: comb<boolean>
  returnFocus: string
  onClose?: () => void
}) => {
  if (!open.val) return null
  return <Screen open={open} {...rest} />
}

const Screen = ({
  open,
  returnFocus,
  onClose,
}: {
  open: comb<boolean>
  returnFocus: string
  onClose?: () => void
}) => {
  const t = useTranslate()
  const scrollRef = useRef<HTMLElement>(null)
  const sdk = useSdk(ErrorLevel.NoError)
  const currentUser = appStore((state) => state.currentUser)

  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
  })

  const track = usePageviewTracking()
  useEffect(() => {
    track("Settings", "settings/privacy")
  }, [])

  useModalDismiss(() => {
    setFocus(returnFocus)
    open.set(false)
    onClose?.()
  })

  useEffect(() => {
    setFocus(SettingsFocusKeys.PrivacyDefault)
  }, [setFocus])

  const tracking = appStore((state) => state.tracking)
  const [switchMux, setSwitchMux] = useState(tracking.mux)
  const handleSwitchMux = (e: boolean) => {
    setSwitchMux(e)
    setConsent({ type: "mux", value: e })
  }
  const [switchMixpanel, setSwitchMixpanel] = useState(tracking.mixpanel)
  const handleSwitchMixpanel = (e: boolean) => {
    setSwitchMixpanel(e)
    setConsent({ type: "mixpanel", value: e })
  }
  const [switchSentry, setSwitchSentry] = useState(tracking.sentry)
  const handleSwitchSentry = (e: boolean) => {
    setSwitchSentry(e)
    setConsent({ type: "sentry", value: e })
  }

  const scrollFn = useCallback(
    (y: number) =>
      hwScroll({
        scrollRef,
        y,
      }),
    [scrollRef],
  )

  const setTracking = appStore((state) => state.setTracking)
  const setConsent = ({ type, value }: SetTrackingProps) => {
    setTracking({ type, value })
    if (type === "mixpanel" && currentUser?.id)
      sdk.consentTracking({
        input: {
          consent: value ? "ALLOW" : "REVOKE",
          service: "MIXPANEL",
        },
      })
  }

  const transition = useTransitions()

  return (
    <Modal
      open={open.val}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.variant,
        "& > *": { transition: "none !important" },
      }}
    >
      <DialogContent>
        <Box
          ref={scrollRef}
          style={{ transition }}
          sx={{
            height: "auto",
            width: "100%",
            backgroundColor: theme.palette.primary.variant,
            position: "absolute",
            overflowY: "visible",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <FocusContext.Provider value={focusKey}>
              <Box
                ref={ref}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: s("1160px"),
                  mt: s("184px"),
                  "& > :not(:last-child)": {
                    mb: s("60px"),
                  },
                }}
              >
                <FocusableParagraph
                  aria-label={`${t("tracking__advanced_settings_header")}: ${t("privacy__tracking_optional_text")}`}
                  focusKey={SettingsFocusKeys.PrivacyDefault}
                  scrollFn={scrollFn}
                >
                  <DGText
                    tx={TI.H05tv}
                    sx={{
                      color: theme.palette.white.main,
                      mb: s("32px"),
                    }}
                  >
                    {t("tracking__advanced_settings_header")}
                  </DGText>
                  <PText>{t("privacy__tracking_optional_text")}</PText>
                </FocusableParagraph>
                <ConsentButtons
                  open={open}
                  returnFocus={returnFocus}
                  scrollFn={scrollFn}
                  onClose={onClose}
                />
                <FocusableParagraph
                  aria-label={`${t("privacy__tracking_necessary_title")}: ${t("privacy__tracking_necessary_text")}`}
                  scrollFn={scrollFn}
                >
                  <PTitle>{t("privacy__tracking_necessary_title")}</PTitle>
                  <PText>{t("privacy__tracking_necessary_text")}</PText>
                </FocusableParagraph>
                <SwitchBar
                  scrollFn={scrollFn}
                  disabled={true}
                  onChange={() => {}}
                  checked={true}
                  title={t("privacy__tracking_cloudinary_title")}
                  description={t("privacy__tracking_cloudinary_text")}
                />
                <SwitchBar
                  scrollFn={scrollFn}
                  disabled={true}
                  onChange={() => {}}
                  checked={true}
                  title={t("privacy__tracking_stageplus_title")}
                  description={t("privacy__tracking_stageplus_text")}
                />
                <FocusableParagraph
                  aria-label={`${t("privacy__tracking_optional_title")}: ${t("privacy__tracking_optional_text")}`}
                  scrollFn={scrollFn}
                >
                  <PTitle>{t("privacy__tracking_optional_title")}</PTitle>
                  <PText>{t("privacy__tracking_optional_text")}</PText>
                </FocusableParagraph>
                <SwitchBar
                  scrollFn={scrollFn}
                  onChange={handleSwitchMux}
                  checked={switchMux}
                  title={t("privacy__tracking_mux_title")}
                  description={t("privacy__tracking_mux_text")}
                />
                <SwitchBar
                  scrollFn={scrollFn}
                  onChange={handleSwitchMixpanel}
                  checked={switchMixpanel}
                  title={t("privacy__tracking_mixpanel_title")}
                  description={t("privacy__tracking_mixpanel_text")}
                />
                <SwitchBar
                  scrollFn={scrollFn}
                  onChange={handleSwitchSentry}
                  checked={switchSentry}
                  title={t("privacy__tracking_sentry_title")}
                  description={t("privacy__tracking_sentry_text")}
                />
              </Box>
            </FocusContext.Provider>
          </Box>
          <Footer />
        </Box>
      </DialogContent>
    </Modal>
  )
}
